import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ═══════════════════════════════════════════════════════════════════════════
   1. NotificationBell
   Bell icon inside a bordered circle with optional unread-count badge.
   Matches production Badge component styling exactly.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface NavbarNotificationBellProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Unread count. 0 or undefined hides the badge. */
  count?: number;
  /** Cap the displayed number (e.g., 99). Defaults to 99. */
  maxCount?: number;
  /** Whether the notification panel is currently open. */
  active?: boolean;
  /** Color variant for the icon circle border. @default 'primary' */
  variant?: 'primary' | 'secondary';
  semanticId?: string;
}

const TRANSIENT_BELL = ['$active', '$variant'] as const;
const shouldForwardBell = (prop: string | number): boolean =>
  !TRANSIENT_BELL.includes(prop as typeof TRANSIENT_BELL[number]);

const StyledBellButton = styled.button.withConfig({
  shouldForwardProp: shouldForwardBell,
})<{ $active: boolean; $variant: 'primary' | 'secondary' }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${rem(7)} ${rem(12)};
  border-radius: var(--ui-radius-md, ${rem(6)});
  transition: background 300ms ease;

  .material-symbols-outlined {
    width: ${rem(34)};
    height: ${rem(34)};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${rem(18)};
    font-weight: 900;
    border: 2px solid
      ${({ $variant }) =>
        $variant === 'secondary'
          ? `var(--ui-accent, ${palette.Iris})`
          : `var(--ui-interactive, ${palette.Purple})`};
    color: ${({ $variant }) =>
      $variant === 'secondary'
        ? `var(--ui-accent, ${palette.Iris})`
        : `var(--ui-interactive, ${palette.Purple})`};
    transition: color 300ms ease, border-color 300ms ease;
  }

  &:hover,
  &.active {
    background-color: color-mix(in srgb, var(--ui-interactive, ${palette.Purple}) 10%, transparent);
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: color-mix(
        in srgb,
        var(--ui-interactive, ${palette.Purple}) 10%,
        transparent
      );
    `}
`;

const StyledBadge = styled.span`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--ui-danger, ${palette.Red});
  width: 13px;
  height: 13px;
  border-radius: 50%;
  top: 28px;
  right: 12px;
  font-size: ${rem(8)};
  color: ${palette.White};
  pointer-events: none;
`;

const NavbarNotificationBell = React.forwardRef<HTMLButtonElement, NavbarNotificationBellProps>(
  function NavbarNotificationBell(
    { count, maxCount = 99, active = false, variant = 'primary', semanticId, ...rest },
    ref
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const displayCount = count != null ? (count > maxCount ? `${maxCount}+` : String(count)) : null;

    return (
      <StyledBellButton
        ref={ref}
        type='button'
        $active={active}
        $variant={variant}
        className={active ? 'active' : ''}
        aria-label={displayCount ? `Notifications (${count} unread)` : 'Notifications'}
        {...semanticProps}
        {...rest}>
        <span className='material-symbols-outlined'>notifications</span>
        {displayCount != null && <StyledBadge>{displayCount}</StyledBadge>}
      </StyledBellButton>
    );
  }
);

NavbarNotificationBell.displayName = 'Navbar.NotificationBell';

/* ═══════════════════════════════════════════════════════════════════════════
   2. InstanceSwitch
   Company logo + company name + instance label + dropdown arrow.
   Clicking opens a switch-company/instance modal (consumer wires onClick).
   ═══════════════════════════════════════════════════════════════════════════ */

export interface NavbarInstanceSwitchProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Company or profile image URL. */
  logoUrl?: string;
  /** Company display name. */
  companyName?: string;
  /** Instance label (e.g., "Main", "Sandbox 1"). */
  instanceName?: string;
  /** Whether the dropdown arrow is shown (requires switch/sandbox permission). */
  switchable?: boolean;
  /** Whether the user is on a switched company (shows home icon). */
  isSwitched?: boolean;
  /** Callback for clicking the home icon. */
  onGoHome?: () => void;
  semanticId?: string;
}

const StyledInstanceSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  cursor: pointer;
  max-width: ${rem(220)};
  margin-left: ${rem(20)};
  height: 100%;
`;

const StyledCompanyLogo = styled.div`
  flex-shrink: 0;
  height: ${rem(50)};
  max-width: ${rem(65)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: ${rem(50)};
    width: 100%;
    max-width: ${rem(65)};
    object-fit: contain;
  }
`;

const StyledCompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StyledCompanyName = styled.div`
  font-weight: 700;
  font-size: ${rem(16)};
  line-height: 1.2;
  color: var(--ui-interactive, ${palette.DarkPurple});
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(130)};
`;

const StyledInstanceLabel = styled.div`
  font-weight: 500;
  font-size: ${rem(14)};
  line-height: 1.4;
  color: var(--ui-interactive, ${palette.DarkPurple});
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(130)};
`;

const StyledSwitchArrow = styled.span`
  color: var(--ui-text-subtle, ${palette.Gray});
  flex-shrink: 0;
  font-size: ${rem(20)};
  display: inline-flex;
  align-items: center;
`;

const StyledHomeButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${rem(2)};
  flex-shrink: 0;

  .material-symbols-outlined {
    font-size: ${rem(20)};
    color: var(--ui-interactive, ${palette.Purple});
  }

  &:hover .material-symbols-outlined {
    opacity: 0.8;
  }
`;

const NavbarInstanceSwitch = React.forwardRef<HTMLDivElement, NavbarInstanceSwitchProps>(
  function NavbarInstanceSwitch(
    {
      logoUrl,
      companyName,
      instanceName,
      switchable = false,
      isSwitched = false,
      onGoHome,
      semanticId,
      onClick,
      ...rest
    },
    ref
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <StyledInstanceSwitch
        ref={ref}
        className='NavBarProfilePic'
        onClick={onClick}
        {...semanticProps}
        {...rest}>
        {logoUrl && (
          <StyledCompanyLogo>
            <img src={logoUrl} alt={companyName ?? 'Company'} />
          </StyledCompanyLogo>
        )}

        {(companyName || instanceName) && (
          <StyledCompanyInfo>
            {companyName && (
              <StyledCompanyName title={companyName}>{companyName}</StyledCompanyName>
            )}
            {instanceName && (
              <StyledInstanceLabel id='instanceSwitchLabel' title={instanceName}>
                {instanceName}
              </StyledInstanceLabel>
            )}
          </StyledCompanyInfo>
        )}

        {switchable && (
          <StyledSwitchArrow className='material-symbols-outlined' aria-hidden>
            arrow_drop_down
          </StyledSwitchArrow>
        )}

        {isSwitched && onGoHome && (
          <StyledHomeButton
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onGoHome();
            }}
            aria-label='Go to home company'>
            <span className='material-symbols-outlined'>wifi_home</span>
          </StyledHomeButton>
        )}
      </StyledInstanceSwitch>
    );
  }
);

NavbarInstanceSwitch.displayName = 'Navbar.InstanceSwitch';

/* ═══════════════════════════════════════════════════════════════════════════
   3. UserTrigger
   Styled wrapper: [Name ▼] with border, matching production ProfileDrop.
   Designed to be used as the `children` of Navbar.ProfileMenu.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface NavbarUserTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** User display name. */
  name: string;
  semanticId?: string;
}

const StyledUserTrigger = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  border: 1px solid var(--ui-border, ${palette.LightGray});
  border-radius: var(--ui-radius-md, ${rem(6)});
  user-select: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--ui-border-hover, ${palette.gray[300]});
  }
`;

const StyledUserName = styled.span`
  display: block;
  font-weight: 500;
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  padding: ${rem(10)} ${rem(12)};
  color: var(--ui-text, ${palette.Black});
  white-space: nowrap;

  @media (max-width: 1200px) {
    max-width: ${rem(100)};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledUserArrow = styled.span`
  color: var(--ui-text-subtle, ${palette.Gray});
  font-size: ${rem(22)};
  display: inline-flex;
  align-items: center;
  margin-right: ${rem(7)};
  margin-top: ${rem(5)};
`;

const NavbarUserTrigger = React.forwardRef<HTMLDivElement, NavbarUserTriggerProps>(
  function NavbarUserTrigger({ name, semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledUserTrigger ref={ref} {...semanticProps} {...rest}>
        <StyledUserName>{name}</StyledUserName>
        <StyledUserArrow className='material-symbols-rounded' aria-hidden>
          arrow_drop_down
        </StyledUserArrow>
      </StyledUserTrigger>
    );
  }
);

NavbarUserTrigger.displayName = 'Navbar.UserTrigger';

/* ═══════════════════════════════════════════════════════════════════════════
   4. Spacer
   Thin invisible spacer for spacing between action groups.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface NavbarSpacerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width in pixels (converted to rem). @default 8 */
  width?: number;
  /** Height in pixels (converted to rem). */
  height?: number;
}

const TRANSIENT_SPACER = ['$w', '$h'] as const;
const shouldForwardSpacer = (prop: string | number): boolean =>
  !TRANSIENT_SPACER.includes(prop as typeof TRANSIENT_SPACER[number]);

const StyledSpacer = styled.div.withConfig({
  shouldForwardProp: shouldForwardSpacer,
})<{ $w: number; $h?: number }>`
  flex-shrink: 0;
  width: ${({ $w }) => rem($w)};
  ${({ $h }) => $h != null && `height: ${rem($h)};`}
`;

const NavbarSpacer = React.forwardRef<HTMLDivElement, NavbarSpacerProps>(function NavbarSpacer(
  { width = 8, height, ...rest },
  ref
) {
  return <StyledSpacer ref={ref} $w={width} $h={height} aria-hidden {...rest} />;
});

NavbarSpacer.displayName = 'Navbar.Spacer';

/* ─────────────────────────── Exports ───────────────────────────────────── */

export { NavbarNotificationBell, NavbarInstanceSwitch, NavbarUserTrigger, NavbarSpacer };
